import type { NamespaceCaptureTranslation } from '../../i18n-types';

const de_capture = {
  alertMessages: {
    addLocation: 'Lokalisation hinzufügen',
    cannotDeleteCondition: 'Der Eintrag kann nicht gelöscht werden',
    cannotDeleteLesion: 'Der Eintrag kann nicht gelöscht werden',
    cannotDeleteRash: 'Der Eintrag kann nicht gelöscht werden',
    conditionHasImageRegistered:
      'Dieser Befund enthält Informationen und kann nicht gelöscht werden.',
    leave: 'Verlassen',
    lesionHasImageRegistered:
      'Diese Läsion enthält Informationen und kann nicht gelöscht werden.',
    newClinicalAction: 'Neue klinische Handlung?',
    newClinicalActionOrNewImage:
      'Sie können entweder eine neue klinische Handlung durchführen oder neue Bilder aufnehmen.',
    wantToLeave: 'Sind Sie sicher, dass Sie diese Seite verlassen möchten?',
  },
  blurriness: {
    approvedDefault: 'Ihr Bild wird gespeichert, wenn Sie fortfahren.',
    approvedDermoscopy:
      'Ihr dermatoskopisches Bild wird gespeichert, wenn Sie fortfahren.',
    approvedTwoImages:
      'Zwei Bilder (mit/ohne Blitz) werden gespeichert, wenn Sie fortfahren.',
    blurrinessBad: 'Ist das Bild unscharf?',
    blurrinessGood: 'Gut gemacht!',
    blurrinessUnchecked: 'Ist das Bild scharf?',
    disapprovedDefault:
      'Versuchen Sie, die Linse zu reinigen und die Kamera ruhig zu halten.',
    disapprovedDermoscopy:
      'Zoomen Sie auf die Veränderung, um zu prüfen, ob das Bild scharf ist. Falls nötig, machen Sie ein neues Foto, oder fahren Sie mit diesem fort.',
    disapprovedTwoImages:
      'Nehmen Sie ein neues Bild auf oder fahren Sie fort, wenn die Qualität ausreichend ist.',
    error:
      'Wir konnten die Bildqualität diesmal nicht beurteilen. Sie können das Bild verwenden, wenn die Qualität ausreichend ist.',
    uncheckedDefault:
      'Prüfen Sie, ob das Bild scharf ist. Machen Sie bei Bedarf ein neues Foto, oder fahren Sie fort, um es zu speichern.',
  },
  buttons: {
    addCondition: 'Befund hinzufügen',
    addDifferentCondition: 'Weiteren Befund hinzufügen',
    addImagesAction: 'Bilder/Handlung hinzufügen',
    addLesion: 'Läsion hinzufügen',
    addLocation: 'Lokalisation hinzufügen',
    addRash: 'Ausschlag hinzufügen',
    addSpecimenID: 'Proben-ID manuell hinzufügen',
    addSSN: 'Informationen manuell hinzufügen',
    allLesions: 'Alle Läsionen',
    dermoscope: 'Dermatoskop',
    done: 'Fertig',
    exitDemoMode: 'Demo-Modus verlassen',
    findAddNewPatient: 'Neuen Patienten finden/hinzufügen',
    findOrAddPatient: 'Patienten finden oder hinzufügen',
    gotToHomePage: 'Zur Startseite gehen',
    patientOverview: 'Patientenübersicht',
    recentRegistrations: 'Letzte Registrierungen',
    restartTheApp: 'App neu starten',
    retake: 'Erneut aufnehmen',
  },
  camera: {
    addMoreImages: 'Weitere Bilder',
    closeUpImagesTitle: 'Nahaufnahme{{nr:n}}',
    continue: 'Weiter',
    deleteImage: {
      message:
        'Sind Sie sicher, dass Sie dieses Bild löschen möchten?\n\nDiese Aktion kann nicht rückgängig gemacht werden.',
      title: 'Bild löschen?',
    },
    dermoscopyImagesTitle: 'Dermatoskopisch{{nr:es|e}} Bild{{nr:|er}}',
    overviewImagesTitle: 'Übersichtsbild{{nr:er}}',
  },
  cameraPermissionScreen: {
    actionText: 'Weiter',
    message: 'Diese Funktion erfordert den Zugriff auf die Kamera.',
    title: 'Kamerazugriff',
  },
  captureInstructions: {
    overviewImageTitle: 'Tipps zum Aufnehmen von Übersichtsbildern',
    dermoscopicImageTitle: 'Tipps zum Aufnehmen von dermatoskopischen Bilder',
    closeUpImageTitle: 'Tipps zum Aufnehmen von Nahaufnahmen',
    positionTheCameraAtCorrectDistance:
      'Kamera in der richtigen Entfernung positionieren',
    ifCameraTooClose:
      'Ist die Kamera <>zu nah<>, kann das Bild unscharf werden und bietet keinen guten Überblick über den Hautbereich um die Läsion.',
    ifCameraTooFar:
      'Ist die Kamera <>zu weit entfernt<>, werden die Details der Läsion beim Hineinzoomen auf einem großen Bildschirm unscharf.',
    ensureTheImageIsSharpAndFocused:
      'Sicherstellen, dass das Bild scharf und fokussiert ist',
    alwaysTapLesionAndWaitForYellowSquare:
      '<>Die Läsion auf dem Bildschirm antippen<>, um die Kamera dort zu fokussieren. Warten, bis der gelbe Rahmen erscheint.',
    ensureLesionInCenter:
      'Wenn möglich, darauf achten, dass <>die Läsion in der Bildmitte<> liegt, markiert durch weiße Klammern.',
    zoomInToEnsureSharpness:
      'Nach der Aufnahme <>hineinzoomen<>, um sicherzustellen, dass die Läsion scharf ist – Unschärfe ist auf dem kleinen Handydisplay schwer zu erkennen.',
    stillGettingPoorQualityImages: 'Immer noch schlechte Bildqualität?',
    ensureEnoughLight: 'Sicherstellen, dass <>viel Licht im Raum<> ist.',
    keepCameraStill:
      'Achten Sie darauf, die <>Kamera ruhig zu halten<>, wenn das Bild aufgenommen wird.',
    cleanCameraLens:
      '<>Kameralinse reinigen<>, wenn die Bilder weiterhin unscharf sind.',
    attachDermoscopeAndTurnItOn: 'Dermatoskop anbringen und einschalten',
    pressDermliteButton:
      'Drücken Sie den roten Knopf an der Seite des Dermatoskops, um es einzuschalten.',
    onePressForPolarisedLight:
      'Einmal drücken = Polarisationsmodus (empfohlen). <>Doppelklick vermeiden, außer wenn der nicht-polarisierte Modus benötigt wird.<>',
    pressMelascopeButton:
      'Drücken Sie den schwarzen Knopf an der Seite des Dermatoskops, um es einzuschalten.',
    enablePolarisedLight:
      'Aktivieren Sie das polarisierte Licht für eine optimale Bildqualität, falls Ihr Dermatoskop dies unterstützt.',
    useEnoughImmersionFluid: 'Genügend Immersionsflüssigkeit verwenden',
    applyImmersionFluidGenerously:
      'Großzügig Alkohol oder Öl zwischen Dermatoskop und Haut auftragen.',
    immersionFluidImportance:
      'Besonders wichtig, bei <>Make-up oder trockener, schuppiger Haut<>.',
    alwaysUseImmersionFluidInNonPolarisedMode:
      '<>Immer Immersionsflüssigkeit (Alkohol oder Öl)<> im nicht-polarisierten Modus verwenden – ohne sie haben die Bilder kaum diagnostischen Wert.',
    restDermoscopeDirectlyOnSkin:
      'Das Dermatoskop <>direkt auf die Haut<> aufsetzen.',
    doNotPressDermoscopeTooHard:
      'Nicht zu fest drücken, da dies das Blut in den kleinen Gefäßen um die Läsion verdrängen kann. Auch bei erhabenen Läsionen vorsichtig sein. Immer die Läsion auf dem Bildschirm antippen, um korrekt zu fokussieren.',
    alwaysTapLesion:
      'Immer die Läsion auf dem Bildschirm antippen, um korrekt zu fokussieren.',
    ensureContactWithSkin:
      'Sicherstellen, dass Kontakt zwischen Dermatoskop und Haut besteht.',
    cleanCameraAndDermoscopeLens:
      '<>Kamera- und Dermatoskoplinsen reinigen<>, wenn die Bilder weiterhin unscharf sind.',
    holdCameraFarEnough:
      'Kamera weit genug halten, um den Ausschlag und umliegende gesunde Haut einzufangen, um vollständigen Diagnosekontext zu bieten.',
    alwaysTapRashAndWaitForYellowSquare:
      'Immer den Ausschlag antippen, um die Kamera zu fokussieren – warten, bis der gelbe Rahmen erscheint.',
    placeRashInCenter:
      'Wenn möglich, <>den Ausschlag in die Bildmitte<> setzen, markiert durch weiße Klammern.',
    alwaysTapRash:
      'Immer den Ausschlag auf dem Bildschirm antippen, um korrekt zu fokussieren.',
    includeAsMuchSkinAsPossible: 'So viel Haut wie möglich einbeziehen',
    includeAsMuchSkinAsPossibleInImage:
      'So viel Haut wie möglich im Bild einbeziehen, z. B. beide Arme.',
    moveCameraAwayForGoodImage:
      'Kamera weiter weg halten, um ein gutes Bild zu erhalten',
    recommendedPolarizedLight: 'Empfohlenes polarisiertes Licht',
    nonPolarizedLight: 'Nicht-polarisiertes Licht',
    enoughImmersionFluid: 'Genug\nImmersionsflüssigkeit',
    tooLittleImmersionFluid: 'Zu wenig\nImmersionsflüssigkeit',
    footerText:
      'Wenn Sie alle obigen Richtlinien befolgt haben und weiterhin schlechte Bilder erhalten – kontaktieren Sie unseren Support unter +45 44 40 82 45',
  },
  clinicalActions: {
    biopsy: 'Biopsie',
    biopsyActionText:
      '{biopsyTypeName|{Curretage: Kürettagebiopsie, Excision: Exzisionsbiopsie, Incision: Inzisionsbiopsie, Punch: Stanzenbiopsie, Shave: Shave-Biopsie, *: Nicht spezifizierte Biopsie}}',
    clinicalAction: 'Klinische Handlung',
    dismissal: 'Keine weitere Handlung',
    followUpAssessment: 'Verlaufskontrolle',
    newImageRegistration: 'Neue Bildaufnahme',
    noAction: 'Keine Handlung',
    notSpecified: 'Nicht spezifiziert',
    plannedBiopsy: 'Geplante Biopsie',
    referral: 'Überweisung',
    referralActionText:
      '{referralType|{physical: Physische Überweisung, *: eKonsil}}',
  },
  consentAlert: {
    clickHere: 'durch Drücken hier.',
    consentAllOnSave: 'Allen zustimmen und speichern',
    description: 'Wir möchten Ihre Zustimmung zu Folgendem einholen',
    mandatory: 'Pflichtfeld',
    save: 'Meine Auswahl speichern',
    title: 'Einwilligung',
  },
  deleteUser: {
    accountDeletionWithinAMonth:
      'Die Accountlöschung wird manuell durch das Melatech-Team innerhalb eines Monats durchgeführt.',
    and: 'und',
    deleteAccount: 'Account löschen',
    demrloopLearn: 'Dermloop Learn',
    dermloopCapture: 'Dermloop Capture',
    dermloopDesktop: 'Dermloop Desktop',
    ifYouRegret: 'wenn Sie es sich anders überlegen',
    ifYouRegretYourAccountDeletion:
      'Wenn Sie es sich anders überlegen und Ihren Account doch behalten möchten, melden Sie sich erneut an, bevor er gelöscht wird. Sie werden gefragt, ob Sie die Löschung widerrufen machen möchten.',
    registeredAccountDeletion:
      'Wir haben Ihre Anfrage zur Accountlöschung registriert. Wir werden Ihren Account löschen',
    requestDeletion: 'Möchten Sie Ihren Account löschen?',
    requestReceived: 'Anfrage erhalten',
    thisWillDeleteIn: 'Beachten Sie, dass Ihren Account gelöscht wird in',
    withinAMonth: 'Innerhalb eines Monats',
  },
  demo: {
    alert: {
      button: {
        accept: 'Weiter',
        exit: 'Demo beenden',
      },
      dataDeletionPolicy:
        'Die eingegebenen Daten werden nach 24 Stunden gelöscht.',
      demoModeConfirmation: 'Sie befinden sich im <>Dermloop Demo<>-Modus.',
      testPatientDisclaimer:
        'Dies ist ein <>TEST<>-Patient. Die von Ihnen registrierten Läsionen oder Ausschläge werden nicht ausgewertet.',
      title: 'Dermloop Demo',
    },
    bannerTitle: 'Dermloop Demo',
    enteringModeAlert: {
      button: {
        accept: 'Weiter',
        exit: 'Demo beenden',
      },
      dataResetPolicy:
        'Die eingegebenen Daten werden nach 24 Stunden zurückgesetzt.',
      entryNotice:
        'Bitte beachten Sie, dass Sie gleich in den Dermloop Demo-Modus wechseln.',
      testPatientDisclaimer:
        'Das bedeutet, dass es sich um einen <>TEST<>-Patienten handelt und die erfassten Daten nicht ausgewertet oder berücksichtigt werden.',
      title: 'Dermloop Demo',
    },
    exitDemoMode: 'Demo-Modus verlassen',
    testPatient: 'Testpatient',
  },
  dermoscope: {
    includeSkin: 'So viel Haut wie möglich einbeziehen',
    includeSurrounding:
      'Achten Sie darauf, dass auch der umgebende Hautbereich erfasst wird.',
    moveCameraAway: 'Kamera weiter entfernen, um ein gutes Bild zu erhalten',
    liveWarnings: {
      dermoscopeTurnedOff: 'Das Dermatoskop ist ausgeschaltet',
      dermoscopeTurnedOffMessageDermlite:
        'Drücken Sie einmal die Taste, um polarisiertes Licht einzuschalten.',
      dermoscopeTurnedOffMessageMelascope: 'Einschalten um fortzufahren.',
      nonPolarisedLight: 'Polarisiertes Licht nicht erkannt',
      nonPolarisedLightMessage:
        'Bitte stellen Sie sicher, dass polarisiertes Licht für eine optimale Bildqualität aktiviert ist.',
      readyToCapture: 'Bereit zum Aufnehmen',
      readyToCaptureMessageDermlite:
        'Dermatoskop ist eingeschaltet und polarisiertes Licht aktiv.',
      readyToCaptureMessageMelascope: 'Dermatoskop ist eingeschaltet.',
      generalInstruction: 'Erinnerung',
      generalInstructionMessage:
        'Bitte stellen Sie sicher, dass das Dermatoskop eingeschaltet und im Polarisationsmodus ist.',
    },
    imageWarnings: {
      dermoscopeNotAttached: 'Dermatoskop ist nicht verbunden',
      dermoscopeNotAttachedMessage:
        'Dermatoskop anbringen und durch einmaliges Drücken des roten Knopfes an der Seite des Geräts einschalten.',
      dermoscopeTurnedOff: 'Dermatoskop ist ausgeschaltet',
      dermoscopeTurnedOffMessage:
        'Schalten Sie das Dermatoskop ein, indem Sie einmal den roten Knopf an der Seite drücken.',
      nonPolarisedLight: 'Polarisiertes Licht nicht erkannt',
      nonPolarisedLightMessage:
        'Aktivieren Sie das polarisierte Licht, um die bestmögliche Bildqualität zu erhalten. Schalten Sie das Dermatoskop aus und drücken Sie einmal den roten Knopf, um das polarisierte Licht einzuschalten.',
    },
  },
  descriptors: {
    allLesions: 'Alle Hautveränderungen',
    fetchingPatient: 'Patienteninformationen werden abgerufen',
    histologicalDiagnosis: 'Histologische Diagnose',
    histopathologicalEvaluation: 'Histopathologische Auswertung',
    lastAccessed: 'Zuletzt aufgerufen',
    lastClinicalAction: 'Letzte klinische Handlung',
    lastRegistration: 'Letztes eKonsil',
    lesionDoesntMatch: 'Läsion passt nicht zum Patienten',
    lesionImageBaseline: 'Ausgangsbild',
    location: 'Ort',
    medicalRecord: 'Patientenakte',
    newAction: 'Neue Handlung',
    noLesion: 'Keine Hautveränderung',
    noPatient: 'Kein Patient',
    patientInfo: 'Patienteninformation',
    patientOverview: 'Patientenübersicht',
    specimenID: 'Proben-ID',
    teleEvaluation: 'Dermatologische Einschätzung',
    tentativeClinicalDiagnosis: 'Klinische Verdachtsdiagnose',
  },
  desktopQrLogin: {
    buttonText: 'QR-Code auf Computer',
    scannerTitle: 'QR-Code auf Computer scannen',
    invalidCode: 'Ungültiger QR-Code',
  },
  dictation: {
    smartDictation: 'Smart-Diktat',
    stop: 'Stopp',
    startDictation: 'Diktat starten',
    stopDictation: 'Diktat stoppen',
    cleaningUp: 'Wird verarbeitet…',
    errorPrefix: 'Diktatfehler:',
    formatFailed:
      'Diktat konnte nicht formatiert werden. Bitte erneut versuchen.',
    micPermissionTitle: 'Mikrofonzugriff erforderlich',
    micPermissionBody:
      'Für das Smart-Diktat wird Zugriff auf Ihr Mikrofon benötigt. Wir speichern Ihren Entwurf, bevor die Einstellungen geöffnet werden, damit Sie nahtlos weiterarbeiten können.',
    micPermissionCancel: 'Abbrechen',
    micPermissionOpenSettings: 'Einstellungen öffnen',
    micPermissionDenied: 'Kein Zugriff auf das Mikrofon',
    audioSessionFailed: 'Audiositzung konnte nicht aktiviert werden',
    recordingStartFailed: 'Aufnahme konnte nicht gestartet werden',
    recordingUnknownError: 'Unbekannter Aufnahmefehler',
  },
  errorMessages: {
    anError: 'Ein Fehler ist aufgetreten',
    anErrorHappened: 'Ein Fehler ist aufgetreten',
    cannotCreateLesion: 'Fall kann nicht erstellt werden',
    caseUnavailable:
      'Es ist ein Fehler aufgetreten, oder der Fall, den Sie öffnen möchten, existiert nicht mehr.',
    cannotCreateRash:
      'Fall kann nicht erstellt werden, da der Patient nicht gespeichert wurde',
    cannotDeleteLesion:
      'Fall mit ausstehender Handlung kann nicht gelöscht werden.',
    cannotDeletePatientInProgress:
      'Patient in Bearbeitung kann nicht gelöscht werden',
    creatingDetails: 'Problem beim Erstellen der {type}-Details',
    creatingHealthRecordConsent:
      'Problem beim Erstellen der Einwilligung zur Patientenakte',
    createHealthRecord:
      'Patientenakte konnte nicht erstellt werden. Bitte Eingaben prüfen und erneut versuchen.',
    creatingLesion: 'Problem beim Erstellen der Läsion',
    creatingRash: 'Problem beim Erstellen des Ausschlags',
    creatingTriage: 'eKonsil kann nicht erstellt werden',
    error: 'Fehler',
    errorAlternativeImage: 'Fehler beim Hochladen des alternativen Bildes',
    errorOccurredWhileUpdating: 'Beim Aktualisieren ist ein Fehler aufgetreten',
    errorPrimaryImage: 'Fehler beim Hochladen des Hauptbildes',
    errorWhileDeletingImage: 'Fehler beim Löschen des Bildes',
    errorWhileGettingImage: 'Fehler beim Abrufen des {imagetype}-Bildes',
    errorWhileUploading: 'Fehler beim Hochladen des {imagetype}-Bildes',
    gettingHealthRecord: 'Fehler beim Abrufen des Patienten',
    gettingUser: 'Fehler beim Abrufen des Benutzers',
    healthRecords: 'Fehler beim Abrufen der Patientenakten',
    inactiveLesion: 'Inaktive Läsion konnte nicht aktualisiert werden.',
    missingConditionType: 'Es scheint, dass der Befundtyp fehlt',
    newHealthRecord:
      'Beim Erstellen des neuen Patienten ist ein Fehler aufgetreten',
    newPatient:
      'Ein Problem ist beim Erstellen des neuen Patienten aufgetreten',
    updatingPatient:
      'Beim Aktualisieren der Patienteninformationen ist ein Problem aufgetreten.',
    nonmatchigPatient:
      'Aktualisierung der Registrierung fehlgeschlagen: Patientendaten stimmen nicht überein',
    someError: 'Ein Fehler ist aufgetreten',
    somethingWrong: 'Etwas ist schiefgelaufen',
    unauthorized: 'Nicht autorisiert',
    updatingDetails: 'Problem beim Aktualisieren der {type}-Details',
    updatingLesion: 'Fehler beim Aktualisieren der Läsion',
    updatingRash: 'Fehler beim Aktualisieren des Ausschlags',
    patientIsDead: 'Die eingegebene CPR gehört zu einer verstorbenen Person',
  },
  caseEditor: {
    editCase: 'Fall bearbeiten',
    save: 'Speichern',
    saveAndSendTeleReferral: 'Speichern und Telekonsil senden',
    saveAndRecordActions:
      'Speichern und {count} {{Maßnahme|Maßnahmen}} erfassen',
    clinicalActions: 'Klinische Maßnahmen',
    undo: 'Rückgängig',
    cancelReferral: 'Überweisung stornieren',
    keepReferral: 'Überweisung behalten',
    cancelReferralPrompt: {
      title: 'Diese Überweisung stornieren?',
      message: 'Die Überweisung wird beim Speichern des Falls storniert.',
    },
    status: {
      awaitingAnswer: 'Wartet auf Antwort',
      answered: 'Beantwortet',
      cancelled: 'Storniert',
      closed: 'Abgeschlossen',
      recorded: 'Erfasst',
    },
    saveAndCancelTeleReferral: 'Speichern und Telekonsil stornieren',
    saveAndRemoveActions:
      'Speichern und {count} {{Maßnahme|Maßnahmen}} entfernen',
    saveAndApplyChanges:
      'Speichern und {count} {{Änderung|Änderungen}} übernehmen',
    removeAction: '{action} entfernen',
    removedOnSave: 'Wird beim Speichern entfernt',
    cancelledOnSave: 'Wird beim Speichern storniert',
    sentOn: 'Gesendet am {date}',
    sentOnBy: 'Gesendet am {date} von {actor}',
    followUpMonths: '{count} {{Monat|Monate}}',
    newClinicalAction: 'Neue klinische Maßnahme',
    noNewAction: 'Keine neue Maßnahme',
    anotherAction: 'Noch einmal {action}',
    actionInstance: '{action} {number}',
    limitReason: {
      teleReferralAwaitingAnswer:
        'Ein Telekonsil wartet bereits auf eine Antwort. Der Facharzt sieht diese Änderungen.',
      alreadyConcluded: 'Dieser Fall ist bereits abgeschlossen.',
    },
    discardChanges: 'Änderungen verwerfen',
    discardPrompt: {
      title: 'Änderungen verwerfen?',
      message:
        'Ihre Änderungen an diesem Fall wurden nicht gespeichert und gehen verloren.',
    },
    missingImages: {
      title: 'Erforderliche Bilder fehlen',
      message:
        'Einige erforderliche Bilder wurden noch nicht aufgenommen. Nehmen Sie sie jetzt auf oder speichern Sie den Entwurf und ergänzen Sie sie später.',
      saveDraftAnyway: 'Entwurf trotzdem speichern',
    },
    staleEdit: {
      title: 'Der Fall wurde geändert',
      message:
        'Nach Beginn Ihrer Bearbeitung wurde eine neuere Version dieses Falls gespeichert, daher konnten Ihre Änderungen nicht gespeichert werden. Sie werden weiterhin angezeigt. Laden Sie den Fall neu, um die neuesten Informationen zu sehen.',
      reload: 'Neu laden',
    },
  },
  formRenderer: {
    photoRemoval: {
      deleteTitle: 'Dieses Foto endgültig löschen?',
      deleteMessage:
        'Es wird aus dem Fall entfernt, auch aus seinem Verlauf. Dies kann nicht rückgängig gemacht werden.',
      deleteRefused:
        'Dieses Foto kann nicht mehr gelöscht werden und bleibt im Fall.',
    },
    addAnotherImage: 'Noch ein Bild hinzufügen',
    addImages: 'Bilder hinzufügen',
    cameraPermissionDenied:
      'Kamerazugriff wird zum Aufnehmen von Bildern benötigt. Aktivieren Sie ihn in den Einstellungen.',
    cameraTip: 'Auf gute Beleuchtung und scharfen Fokus achten',
    cervixCameraTip: 'Nah herangehen und beim Aufnehmen stillhalten',
    cameraUnavailable: 'Auf diesem Gerät ist keine Kamera verfügbar.',
    captureFailed:
      'Das Foto konnte nicht aufgenommen werden. Zum erneuten Versuchen tippen.',
    uploadFailed: 'Hochladen fehlgeschlagen',
    completeRegistration: 'Eingabe abschließen',
    conclude: 'Abschließen',
    continueToNextImage: 'Weiter zum nächsten Bild',
    continueToNextLocation: 'Weiter zur nächsten Position',
    deleteImage: 'Bild löschen',
    guideExamples: 'Beispiele für ein gutes und ein schlechtes Foto',
    help: {
      positionThePatient: 'Positionieren Sie den Patienten',
      tips: {
        avoidObstruction:
          'Achten Sie darauf, dass Finger oder andere Gegenstände die Sicht nicht verdecken.',
        checkSharpnessAfterCapture:
          'Zoomen Sie nach der Aufnahme hinein, um die Schärfe zu prüfen — Unschärfe ist auf einem kleinen Bildschirm schwer zu erkennen.',
        ensureGoodLighting:
          'Sorgen Sie für ausreichend gleichmäßiges Licht, und vermeiden Sie starke Schatten.',
        fillTheFrame:
          'Gehen Sie nah genug heran, damit der Bereich den größten Teil des Bildes ausfüllt.',
        holdSteady:
          'Halten Sie die Kamera ruhig, und warten Sie, bis das Bild scharfgestellt ist, bevor Sie aufnehmen.',
        mucoBuccalLeftFraming:
          'Bitten Sie den Patienten, den Mund zu öffnen und die linke Wange vorsichtig zur Seite zu ziehen, um die Innenseite sichtbar zu machen.',
        mucoBuccalRightFraming:
          'Bitten Sie den Patienten, den Mund zu öffnen und die rechte Wange vorsichtig zur Seite zu ziehen, um die Innenseite sichtbar zu machen.',
        mucoLesionFraming:
          'Zentrieren Sie den Befund im Bild, und gehen Sie nah genug heran, um feine Details zu erfassen.',
        mucoLipsClosedFraming:
          'Bitten Sie den Patienten, die Lippen sanft in einer entspannten, natürlichen Position zu schließen.',
        mucoMouthFloorFraming:
          'Bitten Sie den Patienten, die Zunge zum Gaumen gehoben zu halten, damit der Mundboden darunter vollständig sichtbar ist.',
        mucoOcclusionGumsFraming:
          'Bitten Sie den Patienten, sanft zuzubeißen, sodass Ober- und Unterkiefer sowie das Zahnfleisch sichtbar sind.',
        mucoPalateFraming:
          'Bitten Sie den Patienten, den Kopf nach hinten zu neigen und den Mund weit zu öffnen, damit der Gaumen deutlich sichtbar ist.',
        mucoTongueDorsumFraming:
          'Bitten Sie den Patienten, die Zunge herauszustrecken und flach zu halten, damit die Oberseite vollständig sichtbar ist.',
        mucoTongueLateralLeftFraming:
          'Bitten Sie den Patienten, die Zunge zu drehen, um die linke Seite zu zeigen, bei Bedarf mit Mullbinde zum Festhalten.',
        mucoTongueLateralRightFraming:
          'Bitten Sie den Patienten, die Zunge zu drehen, um die rechte Seite zu zeigen, bei Bedarf mit Mullbinde zum Festhalten.',
        mucoTongueVentralFraming:
          'Bitten Sie den Patienten, die Zunge zum Gaumen zu heben, damit die Unterseite das Bild ausfüllt.',
      },
      titles: {
        generic: 'Tipps für ein gutes Foto',
        mucoBuccalLeft: 'Tipps zur Aufnahme der Innenseite der linken Wange',
        mucoBuccalRight: 'Tipps zur Aufnahme der Innenseite der rechten Wange',
        mucoLesion: 'Tipps zur Nahaufnahme des Befunds',
        mucoLipsClosed: 'Tipps zur Aufnahme geschlossener Lippen',
        mucoMouthFloor: 'Tipps zur Aufnahme des Mundbodens',
        mucoOcclusionGums: 'Tipps zur Aufnahme von Biss und Zahnfleisch',
        mucoPalate: 'Tipps zur Aufnahme des Gaumens',
        mucoTongueDorsum: 'Tipps zur Aufnahme der Zungenoberseite',
        mucoTongueLateralLeft: 'Tipps zur Aufnahme der linken Zungenseite',
        mucoTongueLateralRight: 'Tipps zur Aufnahme der rechten Zungenseite',
        mucoTongueVentral: 'Tipps zur Aufnahme der Zungenunterseite',
      },
    },
    imagePlural: 'Bilder',
    imageSingular: 'Bild',
    imagesProgress: 'Bilder',
    locationHelp: {
      title: 'So markieren Sie eine Position',
      chooseSurface: 'Oberfläche auswählen',
      chooseSurfaceDescription:
        'Wählen Sie die Zeichnung aus, die zeigt, wo sich der Befund befindet.',
      exploreModel: 'Modell drehen und zoomen',
      exploreModelDescription:
        'Ziehen Sie, um das Modell zu drehen. Ziehen Sie zwei Finger auseinander oder zusammen, um hinein- oder herauszuzoomen.',
      placeMarker: 'Markierung setzen oder verschieben',
      placeMarker2dDescription:
        'Tippen Sie auf die Zeichnung, um die Markierung zu setzen. Tippen Sie erneut, um sie zu verschieben. Die Markierung wird <>grün<>, sobald die Position ausgewählt ist.',
      placeMarker3dDescription:
        'Tippen Sie auf das Modell, um die Markierung zu setzen. Tippen Sie erneut, um sie zu verschieben. Die Markierung wird <>grün<>, sobald die Position ausgewählt ist.',
      save: 'Speichern und fortfahren',
      saveDescription:
        'Prüfen Sie, ob die grüne Markierung richtig platziert ist, und speichern Sie oder fahren Sie mit der nächsten Position fort.',
    },
    notePlaceholder: 'Weitere Details hier angeben...',
    markerOnThisSurface: 'Markierung hier gesetzt',
    noMarkerOnThisSurface: 'Keine Markierung hier',
    modelUnavailable:
      'Das 3D-Modell ist in dieser Version der App nicht verfügbar',
    placeMarkerHint:
      'Tippen Sie auf die Illustration, um die Markierung zu setzen',
    placeMarkerHint3d: 'Tippen Sie auf das Modell, um die Markierung zu setzen',
    placeMarkerTitle:
      'Tippen Sie auf die Zeichnung, um die Markierung zu setzen',
    placeMarkerTitle3d:
      'Tippen Sie auf das Modell, um die Markierung zu setzen',
    saveImagesToJournal: 'Bilder in der Dermloop-Akte speichern',
    saveLocationToJournal: 'In der Dermloop-Akte speichern',
    selectAllThatApply: 'Alle zutreffenden auswählen',
    sendTeleReferral: 'Telekonsil senden',
    showLocation: 'Position anzeigen',
    takePhoto: 'Foto aufnehmen',
    unsupportedWidget:
      'Dieses Formular enthält eine Frage, die diese App-Version nicht anzeigen kann. Bitte aktualisieren Sie die App.',
  },
  headerElements: {
    recentPatients: 'Letzte Patienten',
  },
  images: {
    addImages: 'Bilder hinzufügen',
    addImagesAndLocation: 'Bilder und Lokalisation hinzufügen',
    additionalImages: 'Zusätzliche Bilder',
    takeImages: 'Bilder aufnehmen',
  },
  loadingMessage: {
    creatingNewLesion: 'Neue Läsion wird erstellt',
    creatingNewRash: 'Neuer Ausschlag wird erstellt',
    uploadingImages: 'Bilder werden hochgeladen... Bitte warten.',
    weAreRetrievingPatientInfo:
      'Patienteninformationen werden abgerufen, das sollte nicht lange dauern',
  },
  login: {
    title: 'Auf diesem Gerät einloggen',
    subtitle:
      'Verbinden Sie das Gerät mit Ihrer Sitzung am Computer, um zu beginnen',
    scanAndLogIn: 'Anmelden',
    openQrScanner: 'QR-Code scannen',
    noComputer: 'Arbeiten Sie ohne Computer?',
    continueWithEmail: 'Weiter mit E-Mail',
    usePersonalPhone: 'Mit dem eigenen Telefon anmelden',
  },
  pathologyScanner: {
    addButton: 'Nummer manuell hinzufügen',
    helperText: 'Barcode scannen',
  },
  patientScanner: {
    addAddressManually: 'Adresse manuell eingeben',
    addressLabel: 'Adresse',
    addressSectionLabel: 'Geben Sie Adresse und E-Mail des Patienten ein',
    addressSectionTitle: 'Adresse und E-Mail',
    cityLabel: 'Stadt',
    emailLabel: 'E-Mail',
    inputOverviewDiscrepancyMessage:
      'Die Patienten-ID ist bereits einem anderen Patienten zugeordnet. Wenn die Angaben nicht übereinstimmen, kontaktieren Sie bitte den Support unter <>{supportPhone}<>.',
    inputOverviewLoadingTitle: 'Warten auf Daten...',
    inputOverviewTitle: 'Account-Nr. ⦁ {accountNumber}',
    inputOverviewTitleShort: 'Nr. ⦁ {accountNumber}',
    invalidEmail: 'Geben Sie eine gültige E-Mail-Adresse ein',
    invalidPostalCode: 'Geben Sie eine gültige Postleitzahl ein',
    phoneNumberLabel: 'Geben Sie die Telefonnummer des Patienten ein',
    postalCodeLabel: 'Postleitzahl',
    requiredField: 'Dieses Feld ist erforderlich',
    scanAddress: 'Patientenadresse scannen',
  },
  patientCreator: {
    addPatientInfoManually: 'Patienteninformation manuell hinzufügen',
    addAthenaManually: 'Patienteninformation manuell hinzufügen',
    addEcwInfoManually: 'ECW-Information manuell hinzufügen',
    addEpicInfoManually: 'Patienteninformation manuell hinzufügen',
    addSsnManually: 'CPR manuell hinzufügen',
    addTomedoInfoManually: 'Patienteninformation manuell hinzufügen',
    addQuincyInfoManually: 'Patienteninformation manuell hinzufügen',
    addMedisoftwareInfoManually: 'Patienteninformation manuell hinzufügen',
    errorMessage: {
      duplicateMrn: 'Doppelte MRN',
      invalidMrn: 'Ungültige MRN',
      invalidSsn: 'Ungültige CPR',
    },
    labels: {
      birthDate: 'Geburtsdatum',
      firstName: 'Vorname',
      gender: 'Geschlecht',
      lastName: 'Nachname',
      mrn: 'Patienten-ID',
      transgender: 'Transgender',
    },
    scanPatientInfo: 'Patienteninformation scannen',
    scanAthenaInfo: 'Patienteninformation scannen',
    scanEcwInfo: 'Patienteninformation scannen',
    scanEpicInfo: 'Patienteninformation scannen',
    scanSsnOrBarcode: 'CPR oder Barcode scannen',
    scanTomedoInfo: 'Patienteninformation scannen',
    scanQuincyInfo: 'Patienteninformation scannen',
    scanMedisoftwareInfo: 'Patienteninformation scannen',
  },
  plaintext: {
    additionsJoinSameOrder:
      'Weitere Befunde unten gehören zur selben Anordnung.',
    completedEntry: 'Ihr eKonsil wurde erstellt',
    completedPathology:
      'Ihr eKonsil wurde zur histopathologischen Einschätzung gesendet',
    completedSteps: 'Abgeschlossene Schritte',
    completedTeleReferral:
      'Ihr eKonsil wurde zur dermatologischen Einschätzung übermittelt',
    howToContinueFromHere: 'Wie möchten Sie fortfahren?',
    lastAccessed: 'Zuletzt aufgerufen',
    participationInTeleProject: 'Teilnahme am Projekt',
    recentRegistrations: 'Ihre letzten eKonsile werden hier angezeigt',
    saveAsDraft: 'Als Entwurf speichern',
    selectExistingCondition: 'Bestehenden Befund auswählen',
    selectExistingLesion: 'Bestehende Läsion auswählen',
    selectExistingRash: 'Bestehenden Ausschlag auswählen',
    selectSpecialityToRegister: 'Bitte wählen Sie die Fachrichtung aus',
    selectTypeConditionToRegister: 'Bitte wählen Sie den Befundtyp aus',
    signOrderInEpicToSubmit:
      'Signieren Sie die Anordnung in Epic, um dieses eKonsil zu übermitteln',
    unit: '{unit}',
    youCanEditAndFinalizeLater: 'Sie können später bearbeiten und abschließen',
  },
  scanner: {
    input: {
      confirmPatient: 'Patient bestätigen',
      fillingInInfo: 'Information eingeben',
      infoEntered: 'Information eingegeben',
      invalidInfo: 'Ungültige Information',
      patientFound: 'Patient gefunden',
    },
  },
  searchResults: {
    startTypingToSeeMoreResults:
      'Beginnen Sie mit der Eingabe, um weitere Ergebnisse zu sehen',
  },
  conditionOverview: {
    footerButton: {
      goToRegistration: 'Zurück',
      makeNewRegistration: 'Bilder/Handlung hinzufügen',
    },
    thumbnailImage: {
      closeUp: 'Nahaufnahme',
      dermoscopy: 'Dermatoskopie',
      overview: 'Übersicht',
    },
  },
  ssnScanner: {
    inputOverviewLoadingTitle: 'Warten auf Daten...',
    inputOverviewTitle: 'CPR ⦁ {ssn}',
    overviewValues: {
      birthDate: 'Geburtsdatum',
      gender: 'Geschlecht',
      firstName: 'Vorname',
      lastName: 'Nachname',
      isNameProtected: '*****',
    },
  },
  secondImageWarning: {
    title: 'Moment - ist das dieselbe Läsion?',
    message:
      'Mehrere Bilder <>derselben Läsion<> sind in Ordnung - aber <>eine neue Läsion<> verdient ihren eigenen Fall.',
    note: 'Schließen Sie diesen Fall ab und fügen Sie die neue Läsion über die Läsionsübersicht hinzu.',
    differentLesion: 'Nein, andere Läsion',
    sameLesion: 'Ja, dieselbe Läsion',
  },
  settings: {
    account: 'Konto',
    app: 'App',
    change: 'Wechseln',
    clinic: 'Standort',
    email: 'E-Mail',
    emailLocked:
      'Die E-Mail kann hier nicht geändert werden. Kontaktieren Sie den Support.',
    legal: 'Rechtliches',
    licensesIntro:
      'Dermloop Capture basiert auf Open-Source-Software. Hier sind die enthaltenen Pakete und ihre Lizenzen.',
    name: 'Name',
    openSourceLicenses: 'Open-Source-Lizenzen',
    packageCount: '{count} Paket{{e}}',
    support: 'Support',
    version: 'Version',
  },
  statuses: {
    diagnosed: 'Diagnostiziert',
    noDiagnosis: 'Keine Diagnose',
    noRegistered: 'Keine registrierte Diagnose',
    noRegisteredLesion: 'Keine registrierte Läsion',
    pendingDiagnostic: 'Diagnose ausstehend',
    undiagnosable: 'Nicht diagnostizierbar',
  },
  statusMessages: {
    noAccesToMedicalUnits: 'Sie haben keinen Zugriff auf Standorte.',
    noMatchingPatient: 'Kein passender Patient',
    ressourceNotFound: 'Ressource nicht gefunden',
    scanHealthInsuranceCard: 'PVS scannen',
    scanSSNOrBarcode: 'CPR oder Barcode scannen',
    selectLesionLocationBeforeContinuing:
      'Bitte wählen Sie vor dem Fortfahren eine Lokalisation aus',
    selectRashLocationBeforeContinuing:
      'Bitte wählen Sie vor dem Fortfahren eine Lokalisation aus',
    SSN: 'CPR',
    tapToSelectLesionLocation: 'Tippen Sie, um eine Lokalisation auszuwählen',
    tapToSelectRashLocation: 'Tippen Sie, um eine Lokalisation auszuwählen',
    whenYouCaptureACondition:
      'Wenn Sie einen Befund erfassen, wird er hier angezeigt',
    whenYouCaptureALesion:
      'Wenn Sie eine Läsion erfassen, wird sie hier angezeigt',
    whenYouCaptureARash:
      'Wenn Sie einen Ausschlag erfassen, wird dieser in der Liste angezeigt',
    attachDermatoscopeForDermoscopicImage:
      'Dermatoskop anbringen, um ein dermatoskopisches Bild aufzunehmen',
    detachDermatoscopeForOverviewImage:
      'Dermatoskop entfernen, um ein Übersichtsbild aufzunehmen',
    detachDermatoscopeForCloseUpImage:
      'Dermatoskop entfernen, um ein Nahaufnahmebild aufzunehmen',
    detachDermatoscopeToScanAthenaInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
    detachDermatoscopeToScanEcwInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
    detachDermatoscopeToScanEpicInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
    detachDermatoscopeToScanPatientInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
    detachDermatoscopeToScanSSN:
      'Dermatoskop entfernen, um die CPR-Nummer zu scannen',
    detachDermatoscopeToScanPathologyInfo:
      'Dermatoskop entfernen, um den Barcode zu scannen',
    detachDermatoscopeToScanQRCode:
      'Dermatoskop entfernen, um den QR-Code zu scannen',
    detachDermatoscopeToScanTomedoInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
    detachDermatoscopeToScanQuincyInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
    detachDermatoscopeToScanMedisoftwareInfo:
      'Dermatoskop entfernen, um Patientendaten zu scannen',
  },
  triageRegistration: {
    header: {
      viewImagesOrRecord: 'Bilder/Akte anzeigen',
    },
    loadingMessage: {
      deleting: 'Wird gelöscht...',
    },
    missingInfoAlert: {
      closeUpImage: {
        button: 'Nahaufnahme hinzufügen',
        step1: 'Dermatoskop entfernen',
        step2: 'Kamera zurückziehen und Nahaufnahme erstellen',
        title: 'Nahaufnahme fehlt',
      },
      dermoscopicImage: {
        button: 'Dermatoskopisches Bild hinzufügen',
        step1: 'Dermatoskop anbringen',
        step2: 'Dermatoskopisches Bild der Läsion erstellen',
        title: 'Dermatoskopisches Bild fehlt',
      },
      overviewImageLesion: {
        button: 'Übersichtsbild hinzufügen',
        step1: 'Dermatoskop entfernen',
        step2: 'Übersichtsbild der Läsion und der umliegenden Haut erstellen',
        title: 'Übersichtsbild fehlt',
      },
      overviewImageRash: {
        button: 'Übersichtsbild erstellen',
        step1: 'Dermatoskop entfernen',
        step2: 'Übersichtsbild erstellen und möglichst viel Haut einbeziehen',
        title: 'Übersichtsbild fehlt',
      },
    },
  },
  immersionFluidGuide: {
    title: 'Genug Immersionsflüssigkeit verwenden',
    closeLabel: 'Anleitung schließen',
    applyAlcohol: 'Alkohol als Immersionsflüssigkeit auf die Haut auftragen',
    applyAlcoholDesc:
      'Es minimiert Glanz und macht die tieferen Hautstrukturen für eine präzise Analyse sichtbar.',
    step1: 'Das <>Dermatoskop<> mit einem Alkoholtupfer abwischen',
    step2: 'Die Haut um die <>Läsion<> reinigen',
    step3: 'Das Dermatoskop platzieren, während die Haut <>noch feucht<> ist',
    disclaimer:
      'Diese Anleitung erscheint, wenn Sie zum ersten Mal eine Läsion erfassen oder wenn ein Dermatologe die Bildqualität Ihres letzten Falls wegen fehlender Immersionsflüssigkeit als unzureichend eingestuft hat.',
    understood: 'Verstanden',
  },
  warnings: {
    checkInternetConnection: 'Bitte überprüfen Sie Ihre Internetverbindung',
    credentialsExpired: 'Ihre Zugangsdaten sind abgelaufen',
    reenterPassworf: 'Bitte geben Sie Ihr Passwort erneut ein, um fortzufahren',
    requestNotComplete: 'Wir konnten Ihre Anfrage nicht abschließen',
  },
} satisfies NamespaceCaptureTranslation;

export default de_capture;
